/* ============================================================================
   Cookie-consent storage + helpers (website / maineventswebsite).

   Consent is persisted to localStorage as a versioned object. Bump
   CONSENT_VERSION whenever the cookie/tracking purposes materially change so
   that users are asked to consent again.
   ========================================================================== */

export type ConsentCategory =
  | 'essential'
  | 'functional'
  | 'analytics'
  | 'marketing';

export type ConsentCategories = Record<ConsentCategory, boolean>;

export interface ConsentState {
  version: number;
  timestamp: string;
  categories: ConsentCategories;
}

export const CONSENT_VERSION = 3;
export const CONSENT_STORAGE_KEY = 'seuytel_cookie_consent';

export const OPEN_SETTINGS_EVENT = 'seuytel:open-cookie-settings';
export const CONSENT_CHANGED_EVENT = 'seuytel:cookie-consent-changed';

export const ALL_DENIED: ConsentCategories = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export const ALL_GRANTED: ConsentCategories = {
  essential: true,
  functional: true,
  analytics: true,
  marketing: false,
};

export const SUPPORTED_COOKIE_LANGUAGES = [
  'de',
  'de-AT',
  'en',
  'es',
  'fr',
  'it',
  'uk',
  'no',
  'da',
  'pl',
  'tr',
  'ru',
] as const;

export type CookieLanguage = (typeof SUPPORTED_COOKIE_LANGUAGES)[number];

export const COOKIE_LANGUAGE_STORAGE_KEY = 'seuytel_cookie_lang';

export const COOKIE_LANGUAGE_OPTIONS: {
  code: CookieLanguage;
  flag: string;
  label: string;
}[] = [
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'de-AT', flag: '🇦🇹', label: 'Deutsch (Österreich)' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'it', flag: '🇮🇹', label: 'Italiano' },
  { code: 'uk', flag: '🇺🇦', label: 'Українська' },
  { code: 'no', flag: '🇳🇴', label: 'Norsk' },
  { code: 'da', flag: '🇩🇰', label: 'Dansk' },
  { code: 'pl', flag: '🇵🇱', label: 'Polski' },
  { code: 'tr', flag: '🇹🇷', label: 'Türkçe' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский' },
];

type CategoryMeta = {
  key: ConsentCategory;
  title: string;
  desc: string;
  locked?: boolean;
};

type CookieCopy = {
  title: string;
  intro: string[];
  linksPrefix: string;
  privacy: string;
  policy: string;
  rejectAll: string;
  acceptAll: string;
  settings: string;
  saveSelection: string;
  footerPrivacy: string;
  footerImprint: string;
  footerPolicy: string;
  categories: Record<ConsentCategory, Omit<CategoryMeta, 'key'>>;
};

const DEFAULT_LANGUAGE: CookieLanguage = 'en';

const COPIES: Record<CookieLanguage, CookieCopy> = {
  de: {
    title: 'Wir verwenden Cookies',
    intro: [
      'Main Events verwendet Cookies und ähnliche Technologien, um grundlegende App- und Webfunktionen bereitzustellen, z. B. Login, Warenkorb, Bezahlung, Spracheinstellungen, Karten- und Sitzplatzansichten.',
      'Wir verwenden keine Cookies für Tracking- oder Werbezwecke und verfolgen dich nicht über Apps oder Websites anderer Unternehmen hinweg.',
      'Optionale Statistik-Cookies helfen uns, die Plattform pseudonymisiert zu verbessern. Du kannst optionale Cookies ablehnen oder in den Einstellungen anpassen.',
    ],
    linksPrefix: 'Weitere Informationen findest du in unserer',
    privacy: 'Datenschutzerklärung',
    policy: 'Cookie-Richtlinie',
    rejectAll: 'Alle ablehnen',
    acceptAll: 'Alle akzeptieren',
    settings: 'Einstellungen',
    saveSelection: 'Auswahl speichern',
    footerPrivacy: 'Datenschutzerklärung',
    footerImprint: 'Impressum',
    footerPolicy: 'Cookie-Richtlinie',
    categories: {
      essential: {
        title: 'Essenzielle Cookies',
        desc: 'Notwendig für Login, Warenkorb, Sicherheit und Bezahlung. Diese Cookies können nicht deaktiviert werden.',
        locked: true,
      },
      functional: {
        title: 'Funktionale Cookies',
        desc: 'Speichern Einstellungen wie Sprache, optionale Filter-Region, Event-Entwürfe und Filtereinstellungen ausschließlich auf deinem Gerät. Gemerkte Events bleiben ebenfalls auf dem Gerät und werden bei Anmeldung mit deinem Konto synchronisiert. Standortkarten und Adress-Vervollständigung werden von Google Maps bereitgestellt und nur bei Nutzung geladen.',
      },
      analytics: {
        title: 'Statistik / Analyse',
        desc: 'Hilft uns, die Nutzung der Plattform pseudonymisiert zu verstehen und die App-Funktionalität zu verbessern. Diese Daten werden nicht für Tracking oder Werbung verwendet.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Marketing- und Werbe-Cookies werden nicht verwendet.',
      },
    },
  },

  'de-AT': {
    title: 'Wir verwenden Cookies',
    intro: [
      'Main Events verwendet Cookies und ähnliche Technologien, um grundlegende App- und Webfunktionen bereitzustellen, z. B. Login, Warenkorb, Bezahlung, Spracheinstellungen, Karten- und Sitzplatzansichten.',
      'Wir verwenden keine Cookies für Tracking- oder Werbezwecke und verfolgen dich nicht über Apps oder Websites anderer Unternehmen hinweg.',
      'Optionale Statistik-Cookies helfen uns, die Plattform pseudonymisiert zu verbessern. Du kannst optionale Cookies ablehnen oder in den Einstellungen anpassen.',
    ],
    linksPrefix: 'Weitere Informationen findest du in unserer',
    privacy: 'Datenschutzerklärung',
    policy: 'Cookie-Richtlinie',
    rejectAll: 'Alle ablehnen',
    acceptAll: 'Alle akzeptieren',
    settings: 'Einstellungen',
    saveSelection: 'Auswahl speichern',
    footerPrivacy: 'Datenschutzerklärung',
    footerImprint: 'Impressum',
    footerPolicy: 'Cookie-Richtlinie',
    categories: {
      essential: {
        title: 'Essenzielle Cookies',
        desc: 'Notwendig für Login, Warenkorb, Sicherheit und Bezahlung. Diese Cookies können nicht deaktiviert werden.',
        locked: true,
      },
      functional: {
        title: 'Funktionale Cookies',
        desc: 'Speichern Einstellungen wie Sprache, optionale Filter-Region, Event-Entwürfe und Filtereinstellungen ausschließlich auf deinem Gerät. Gemerkte Events bleiben ebenfalls auf dem Gerät und werden bei Anmeldung mit deinem Konto synchronisiert. Standortkarten und Adress-Vervollständigung werden von Google Maps bereitgestellt und nur bei Nutzung geladen.',
      },
      analytics: {
        title: 'Statistik / Analyse',
        desc: 'Hilft uns, die Nutzung der Plattform pseudonymisiert zu verstehen und die App-Funktionalität zu verbessern. Diese Daten werden nicht für Tracking oder Werbung verwendet.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Marketing- und Werbe-Cookies werden nicht verwendet.',
      },
    },
  },

  en: {
    title: 'We use cookies',
    intro: [
      'Main Events uses cookies and similar technologies to provide essential app and web functions, such as login, cart, payment, language settings, maps, and seat views.',
      'We do not use cookies for tracking or advertising purposes and we do not track you across apps or websites owned by other companies.',
      'Optional statistics cookies help us improve the platform using pseudonymized data. You can reject optional cookies or adjust them in settings.',
    ],
    linksPrefix: 'For more information, see our',
    privacy: 'Privacy Policy',
    policy: 'Cookie Policy',
    rejectAll: 'Reject all',
    acceptAll: 'Accept all',
    settings: 'Settings',
    saveSelection: 'Save selection',
    footerPrivacy: 'Privacy Policy',
    footerImprint: 'Imprint',
    footerPolicy: 'Cookie Policy',
    categories: {
      essential: {
        title: 'Essential Cookies',
        desc: 'Required for login, cart, security, and payment. These cookies cannot be disabled.',
        locked: true,
      },
      functional: {
        title: 'Functional Cookies',
        desc: 'Stores settings like language, optional filter region, event drafts, and filter settings only on your device. Liked events also stay on your device and sync with your account when logged in. Location maps and address autocomplete are provided by Google Maps and load only when used.',
      },
      analytics: {
        title: 'Statistics / Analytics',
        desc: 'Help us understand platform usage with pseudonymized data and improve app functionality. These data are not used for tracking or advertising.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Marketing and advertising cookies are not used.',
      },
    },
  },

  es: {
    title: 'Usamos cookies',
    intro: [
      'Main Events usa cookies y tecnologías similares para ofrecer funciones esenciales de la app y la web, como inicio de sesión, carrito, pagos, idioma, mapas y vistas de asientos.',
      'No usamos cookies con fines de seguimiento o publicidad y no te rastreamos entre apps o sitios web de otras empresas.',
      'Las cookies opcionales de estadísticas nos ayudan a mejorar la plataforma con datos seudonimizados. Puedes rechazar las cookies opcionales o ajustarlas en la configuración.',
    ],
    linksPrefix: 'Más información en nuestra',
    privacy: 'Política de privacidad',
    policy: 'Política de cookies',
    rejectAll: 'Rechazar todo',
    acceptAll: 'Aceptar todo',
    settings: 'Configuración',
    saveSelection: 'Guardar selección',
    footerPrivacy: 'Política de privacidad',
    footerImprint: 'Aviso legal',
    footerPolicy: 'Política de cookies',
    categories: {
      essential: {
        title: 'Cookies esenciales',
        desc: 'Necesarias para inicio de sesión, carrito, seguridad y pagos. No se pueden desactivar.',
        locked: true,
      },
      functional: {
        title: 'Cookies funcionales',
        desc: 'Guardan ajustes como idioma, región opcional de filtros, borradores de eventos y ajustes de filtros solo en tu dispositivo. Los eventos que te gustan también permanecen en tu dispositivo y se sincronizan con tu cuenta al iniciar sesión. Los mapas de ubicación y el autocompletado de direcciones son proporcionados por Google Maps y solo se cargan cuando se usan.',
      },
      analytics: {
        title: 'Estadísticas / análisis',
        desc: 'Nos ayudan a entender el uso de la plataforma con datos seudonimizados y mejorar la funcionalidad. No se usan para seguimiento ni publicidad.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Las cookies de marketing y publicidad no se usan.',
      },
    },
  },

  fr: {
    title: 'Nous utilisons des cookies',
    intro: [
      'Main Events utilise des cookies et technologies similaires pour fournir les fonctions essentielles de l’app et du site, comme la connexion, le panier, le paiement, la langue, les cartes et les plans de sièges.',
      'Nous n’utilisons pas de cookies à des fins de suivi ou de publicité et nous ne vous suivons pas sur les apps ou sites web d’autres entreprises.',
      'Les cookies statistiques optionnels nous aident à améliorer la plateforme avec des données pseudonymisées. Vous pouvez refuser les cookies optionnels ou les ajuster dans les paramètres.',
    ],
    linksPrefix: 'Plus d’informations dans notre',
    privacy: 'Politique de confidentialité',
    policy: 'Politique relative aux cookies',
    rejectAll: 'Tout refuser',
    acceptAll: 'Tout accepter',
    settings: 'Paramètres',
    saveSelection: 'Enregistrer la sélection',
    footerPrivacy: 'Confidentialité',
    footerImprint: 'Mentions légales',
    footerPolicy: 'Cookies',
    categories: {
      essential: {
        title: 'Cookies essentiels',
        desc: 'Nécessaires pour la connexion, le panier, la sécurité et le paiement. Ils ne peuvent pas être désactivés.',
        locked: true,
      },
      functional: {
        title: 'Cookies fonctionnels',
        desc: 'Enregistrent des réglages comme la langue, la région de filtre optionnelle, les brouillons d’événements et les paramètres de filtres uniquement sur votre appareil. Les événements aimés restent aussi sur votre appareil et se synchronisent avec votre compte lors de la connexion. Les cartes de localisation et l’autocomplétion d’adresse sont fournies par Google Maps et ne se chargent que lors de leur utilisation.',
      },
      analytics: {
        title: 'Statistiques / analyse',
        desc: 'Nous aident à comprendre l’utilisation avec des données pseudonymisées et à améliorer les fonctionnalités. Non utilisés pour le suivi ou la publicité.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Les cookies marketing et publicitaires ne sont pas utilisés.',
      },
    },
  },

  it: {
    title: 'Usiamo i cookie',
    intro: [
      'Main Events usa cookie e tecnologie simili per fornire funzioni essenziali dell’app e del sito, come login, carrello, pagamenti, lingua, mappe e viste dei posti.',
      'Non usiamo cookie per tracciamento o pubblicità e non ti tracciamo tra app o siti web di altre aziende.',
      'I cookie statistici opzionali ci aiutano a migliorare la piattaforma con dati pseudonimizzati. Puoi rifiutare i cookie opzionali o modificarli nelle impostazioni.',
    ],
    linksPrefix: 'Maggiori informazioni nella nostra',
    privacy: 'Informativa sulla privacy',
    policy: 'Informativa sui cookie',
    rejectAll: 'Rifiuta tutto',
    acceptAll: 'Accetta tutto',
    settings: 'Impostazioni',
    saveSelection: 'Salva selezione',
    footerPrivacy: 'Privacy',
    footerImprint: 'Note legali',
    footerPolicy: 'Cookie',
    categories: {
      essential: {
        title: 'Cookie essenziali',
        desc: 'Necessari per login, carrello, sicurezza e pagamenti. Non possono essere disattivati.',
        locked: true,
      },
      functional: {
        title: 'Cookie funzionali',
        desc: 'Salvano impostazioni come lingua, regione facoltativa dei filtri, bozze di eventi e impostazioni dei filtri solo sul tuo dispositivo. Anche gli eventi preferiti restano sul tuo dispositivo e si sincronizzano con il tuo account all’accesso. Le mappe di localizzazione e il completamento automatico degli indirizzi sono forniti da Google Maps e vengono caricati solo quando usati.',
      },
      analytics: {
        title: 'Statistiche / analisi',
        desc: 'Ci aiutano a capire l’uso della piattaforma con dati pseudonimizzati e migliorare le funzioni. Non sono usati per tracciamento o pubblicità.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'I cookie di marketing e pubblicità non sono usati.',
      },
    },
  },

  uk: {
    title: 'Ми використовуємо cookie',
    intro: [
      'Main Events використовує cookie та подібні технології для основних функцій застосунку й сайту, зокрема входу, кошика, оплати, мови, карт і схем місць.',
      'Ми не використовуємо cookie для відстеження чи реклами та не відстежуємо вас в інших застосунках або на сайтах інших компаній.',
      'Необов’язкові статистичні cookie допомагають покращувати платформу за допомогою псевдонімізованих даних. Ви можете відхилити їх або змінити налаштування.',
    ],
    linksPrefix: 'Більше інформації у нашій',
    privacy: 'Політиці конфіденційності',
    policy: 'Політиці cookie',
    rejectAll: 'Відхилити все',
    acceptAll: 'Прийняти все',
    settings: 'Налаштування',
    saveSelection: 'Зберегти вибір',
    footerPrivacy: 'Конфіденційність',
    footerImprint: 'Вихідні дані',
    footerPolicy: 'Cookie',
    categories: {
      essential: {
        title: 'Необхідні cookie',
        desc: 'Потрібні для входу, кошика, безпеки та оплати. Їх не можна вимкнути.',
        locked: true,
      },
      functional: {
        title: 'Функціональні cookie',
        desc: 'Зберігають налаштування, як-от мову, необов’язковий регіон фільтра, чернетки подій і параметри фільтрів лише на вашому пристрої. Вподобані події також залишаються на вашому пристрої та синхронізуються з вашим обліковим записом після входу. Карти розташування й автозаповнення адрес надаються Google Maps і завантажуються лише під час використання.',
      },
      analytics: {
        title: 'Статистика / аналітика',
        desc: 'Допомагають зрозуміти використання платформи за допомогою псевдонімізованих даних. Не використовуються для відстеження чи реклами.',
      },
      marketing: {
        title: 'Маркетинг',
        desc: 'Маркетингові та рекламні cookie не використовуються.',
      },
    },
  },

  no: {
    title: 'Vi bruker informasjonskapsler',
    intro: [
      'Main Events bruker informasjonskapsler og lignende teknologi for grunnleggende app- og webfunksjoner som innlogging, handlekurv, betaling, språk, kart og setekart.',
      'Vi bruker ikke informasjonskapsler til sporing eller reklame, og vi sporer deg ikke på tvers av apper eller nettsteder eid av andre selskaper.',
      'Valgfrie statistikkapsler hjelper oss å forbedre plattformen med pseudonymiserte data. Du kan avvise valgfrie kapsler eller endre dem i innstillingene.',
    ],
    linksPrefix: 'Mer informasjon finner du i vår',
    privacy: 'Personvernerklæring',
    policy: 'Cookie-erklæring',
    rejectAll: 'Avvis alle',
    acceptAll: 'Godta alle',
    settings: 'Innstillinger',
    saveSelection: 'Lagre valg',
    footerPrivacy: 'Personvern',
    footerImprint: 'Juridisk informasjon',
    footerPolicy: 'Cookies',
    categories: {
      essential: {
        title: 'Essensielle cookies',
        desc: 'Nødvendige for innlogging, handlekurv, sikkerhet og betaling. Kan ikke deaktiveres.',
        locked: true,
      },
      functional: {
        title: 'Funksjonelle cookies',
        desc: 'Lagrer innstillinger som språk, valgfri filterregion, utkast til arrangementer og filterinnstillinger kun på enheten din. Likte arrangementer blir også på enheten din og synkroniseres med kontoen din når du logger inn. Stedskart og adresseutfylling leveres av Google Maps og lastes kun ved bruk.',
      },
      analytics: {
        title: 'Statistikk / analyse',
        desc: 'Hjelper oss å forstå bruken med pseudonymiserte data og forbedre funksjonaliteten. Brukes ikke til sporing eller reklame.',
      },
      marketing: {
        title: 'Markedsføring',
        desc: 'Markedsførings- og reklamecookies brukes ikke.',
      },
    },
  },

  da: {
    title: 'Vi bruger cookies',
    intro: [
      'Main Events bruger cookies og lignende teknologier til grundlæggende app- og webfunktioner som login, kurv, betaling, sprog, kort og sædevisninger.',
      'Vi bruger ikke cookies til tracking eller reklame, og vi sporer dig ikke på tværs af apps eller websites ejet af andre virksomheder.',
      'Valgfrie statistikcookies hjælper os med at forbedre platformen med pseudonymiserede data. Du kan afvise valgfrie cookies eller ændre dem i indstillingerne.',
    ],
    linksPrefix: 'Læs mere i vores',
    privacy: 'Privatlivspolitik',
    policy: 'Cookiepolitik',
    rejectAll: 'Afvis alle',
    acceptAll: 'Accepter alle',
    settings: 'Indstillinger',
    saveSelection: 'Gem valg',
    footerPrivacy: 'Privatliv',
    footerImprint: 'Impressum',
    footerPolicy: 'Cookies',
    categories: {
      essential: {
        title: 'Nødvendige cookies',
        desc: 'Nødvendige for login, kurv, sikkerhed og betaling. Kan ikke deaktiveres.',
        locked: true,
      },
      functional: {
        title: 'Funktionelle cookies',
        desc: 'Gemmer indstillinger som sprog, valgfri filterregion, udkast til events og filterindstillinger kun på din enhed. Likede events bliver også på din enhed og synkroniseres med din konto, når du logger ind. Stedskort og adresse-autoudfyldning leveres af Google Maps og indlæses kun ved brug.',
      },
      analytics: {
        title: 'Statistik / analyse',
        desc: 'Hjælper os med at forstå brugen med pseudonymiserede data og forbedre funktionaliteten. Bruges ikke til tracking eller reklame.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Marketing- og reklamecookies bruges ikke.',
      },
    },
  },

  pl: {
    title: 'Używamy plików cookie',
    intro: [
      'Main Events używa plików cookie i podobnych technologii do podstawowych funkcji aplikacji i strony, takich jak logowanie, koszyk, płatność, język, mapy i widoki miejsc.',
      'Nie używamy plików cookie do śledzenia ani reklamy i nie śledzimy Cię w aplikacjach ani na stronach innych firm.',
      'Opcjonalne statystyczne pliki cookie pomagają nam ulepszać platformę przy użyciu danych pseudonimizowanych. Możesz je odrzucić lub zmienić w ustawieniach.',
    ],
    linksPrefix: 'Więcej informacji znajdziesz w naszej',
    privacy: 'Polityce prywatności',
    policy: 'Polityce cookies',
    rejectAll: 'Odrzuć wszystkie',
    acceptAll: 'Akceptuj wszystkie',
    settings: 'Ustawienia',
    saveSelection: 'Zapisz wybór',
    footerPrivacy: 'Prywatność',
    footerImprint: 'Informacje prawne',
    footerPolicy: 'Cookies',
    categories: {
      essential: {
        title: 'Niezbędne cookies',
        desc: 'Wymagane do logowania, koszyka, bezpieczeństwa i płatności. Nie można ich wyłączyć.',
        locked: true,
      },
      functional: {
        title: 'Funkcjonalne cookies',
        desc: 'Zapisują ustawienia, takie jak język, opcjonalny region filtrów, szkice wydarzeń i ustawienia filtrów wyłącznie na Twoim urządzeniu. Polubione wydarzenia również pozostają na urządzeniu i synchronizują się z kontem po zalogowaniu. Mapy lokalizacji i autouzupełnianie adresów są dostarczane przez Google Maps i ładowane tylko podczas użycia.',
      },
      analytics: {
        title: 'Statystyka / analiza',
        desc: 'Pomagają nam zrozumieć użycie platformy za pomocą danych pseudonimizowanych i poprawiać funkcje. Nie są używane do śledzenia ani reklamy.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Marketingowe i reklamowe pliki cookie nie są używane.',
      },
    },
  },

  tr: {
    title: 'Çerezleri kullanıyoruz',
    intro: [
      'Main Events; giriş, sepet, ödeme, dil ayarları, haritalar ve koltuk görünümleri gibi temel uygulama ve web işlevleri için çerezler ve benzer teknolojiler kullanır.',
      'Çerezleri takip veya reklam amacıyla kullanmıyoruz ve sizi başka şirketlere ait uygulamalar veya web siteleri arasında takip etmiyoruz.',
      'İsteğe bağlı istatistik çerezleri, takma adlı verilerle platformu iyileştirmemize yardımcı olur. İsteğe bağlı çerezleri reddedebilir veya ayarlardan değiştirebilirsiniz.',
    ],
    linksPrefix: 'Daha fazla bilgi için',
    privacy: 'Gizlilik Politikası',
    policy: 'Çerez Politikası',
    rejectAll: 'Tümünü reddet',
    acceptAll: 'Tümünü kabul et',
    settings: 'Ayarlar',
    saveSelection: 'Seçimi kaydet',
    footerPrivacy: 'Gizlilik',
    footerImprint: 'Yasal bilgiler',
    footerPolicy: 'Çerezler',
    categories: {
      essential: {
        title: 'Zorunlu çerezler',
        desc: 'Giriş, sepet, güvenlik ve ödeme için gereklidir. Devre dışı bırakılamaz.',
        locked: true,
      },
      functional: {
        title: 'İşlevsel çerezler',
        desc: 'Dil, isteğe bağlı filtre bölgesi, etkinlik taslakları ve filtre ayarları gibi ayarları yalnızca cihazınızda saklar. Beğenilen etkinlikler de cihazınızda kalır ve giriş yaptığınızda hesabınızla senkronize edilir. Konum haritaları ve adres otomatik tamamlama Google Maps tarafından sağlanır ve yalnızca kullanıldığında yüklenir.',
      },
      analytics: {
        title: 'İstatistik / analiz',
        desc: 'Takma adlı verilerle platform kullanımını anlamamıza ve işlevleri iyileştirmemize yardımcı olur. Takip veya reklam için kullanılmaz.',
      },
      marketing: {
        title: 'Pazarlama',
        desc: 'Pazarlama ve reklam çerezleri kullanılmaz.',
      },
    },
  },

  ru: {
    title: 'Мы используем cookies',
    intro: [
      'Main Events использует cookies и похожие технологии для основных функций приложения и сайта: входа, корзины, оплаты, языка, карт и схем мест.',
      'Мы не используем cookies для отслеживания или рекламы и не отслеживаем вас в приложениях или на сайтах других компаний.',
      'Необязательные статистические cookies помогают улучшать платформу с использованием псевдонимизированных данных. Вы можете отклонить их или изменить настройки.',
    ],
    linksPrefix: 'Подробнее см. в нашей',
    privacy: 'Политике конфиденциальности',
    policy: 'Политике cookies',
    rejectAll: 'Отклонить все',
    acceptAll: 'Принять все',
    settings: 'Настройки',
    saveSelection: 'Сохранить выбор',
    footerPrivacy: 'Конфиденциальность',
    footerImprint: 'Правовая информация',
    footerPolicy: 'Cookies',
    categories: {
      essential: {
        title: 'Обязательные cookies',
        desc: 'Нужны для входа, корзины, безопасности и оплаты. Их нельзя отключить.',
        locked: true,
      },
      functional: {
        title: 'Функциональные cookies',
        desc: 'Сохраняют настройки, такие как язык, необязательный регион фильтра, черновики событий и параметры фильтров, только на вашем устройстве. Понравившиеся события также остаются на устройстве и синхронизируются с аккаунтом при входе. Карты местоположения и автозаполнение адресов предоставляются Google Maps и загружаются только при использовании.',
      },
      analytics: {
        title: 'Статистика / аналитика',
        desc: 'Помогают понять использование платформы с помощью псевдонимизированных данных и улучшить функции. Не используются для отслеживания или рекламы.',
      },
      marketing: {
        title: 'Маркетинг',
        desc: 'Маркетинговые и рекламные cookies не используются.',
      },
    },
  },
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

const API_BASE = 'https://maineventbackend.seuytel.com/api/';
const TOKEN_KEY = 'me.auth.token';

function urlParam(name: string): string | null {
  if (!isBrowser()) return null;
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

export function isEmbedded(): boolean {
  if (!isBrowser()) return false;
  if (urlParam('webview') === '1' || urlParam('embed') === '1' || urlParam('mode') === 'app') return true;
  try {
    return sessionStorage.getItem('me.embed') === '1';
  } catch {
    return false;
  }
}

export function detectPlatform(): string {
  const forced = urlParam('platform');
  if (forced) return forced.toLowerCase();
  const ua = (isBrowser() && navigator.userAgent) || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/OPR\/|Opera/i.test(ua)) return 'opera';
  if (/Chrome\//i.test(ua)) return 'chrome';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Safari\//i.test(ua)) return 'safari';
  return 'web';
}

export function isMobileAppWebView(): boolean {
  const platform = detectPlatform();
  return isEmbedded() && (platform === 'ios' || platform === 'android');
}

export function isIosAppWebView(): boolean {
  return isMobileAppWebView();
}

export function readStoredCookieLanguage(): CookieLanguage | null {
  if (!isBrowser()) return null;
  try {
    const stored = window.localStorage.getItem(COOKIE_LANGUAGE_STORAGE_KEY);
    if (stored && (SUPPORTED_COOKIE_LANGUAGES as readonly string[]).includes(stored)) {
      return stored as CookieLanguage;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function setCookieUiLanguage(lang: CookieLanguage): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(COOKIE_LANGUAGE_STORAGE_KEY, lang);
  } catch {
    /* storage may be unavailable */
  }
}

export function getCookieLanguage(): CookieLanguage {
  const stored = readStoredCookieLanguage();
  if (stored) return stored;

  const forced = urlParam('lang');
  if (forced && (SUPPORTED_COOKIE_LANGUAGES as readonly string[]).includes(forced)) {
    return forced as CookieLanguage;
  }
  const base = forced?.split('-')[0];
  if (base && (SUPPORTED_COOKIE_LANGUAGES as readonly string[]).includes(base)) {
    return base as CookieLanguage;
  }

  if (!isBrowser()) return DEFAULT_LANGUAGE;
  const browser = navigator.language || DEFAULT_LANGUAGE;
  if ((SUPPORTED_COOKIE_LANGUAGES as readonly string[]).includes(browser)) {
    return browser as CookieLanguage;
  }
  const browserBase = browser.split('-')[0];
  if ((SUPPORTED_COOKIE_LANGUAGES as readonly string[]).includes(browserBase)) {
    return browserBase as CookieLanguage;
  }
  return DEFAULT_LANGUAGE;
}

export function getCookieCopy(): CookieCopy {
  return COPIES[getCookieLanguage()] ?? COPIES[DEFAULT_LANGUAGE];
}

export function getCategoryMeta(): CategoryMeta[] {
  const copy = getCookieCopy();
  const items: CategoryMeta[] = [
    { key: 'essential', ...copy.categories.essential },
    { key: 'functional', ...copy.categories.functional },
    { key: 'analytics', ...copy.categories.analytics },
    { key: 'marketing', ...copy.categories.marketing },
  ];

  return items.filter((item) => item.key !== 'marketing');
}
export function categoriesForCurrentContext(categories: ConsentCategories): ConsentCategories {
  return {
    ...categories,
    essential: true,
    marketing: false,
  };
}

export function deniedForCurrentContext(): ConsentCategories {
  return categoriesForCurrentContext(ALL_DENIED);
}

export function grantedForCurrentContext(): ConsentCategories {
  return categoriesForCurrentContext(ALL_GRANTED);
}

function resolveToken(): string | null {
  const fromUrl = urlParam('token');
  if (fromUrl) return fromUrl;
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function buildConsentEntry(state: ConsentState) {
  return {
    categories: categoriesForCurrentContext(state.categories),
    platform: detectPlatform(),
    user_agent: (isBrowser() && navigator.userAgent) || null,
    source: isEmbedded() ? 'app-webview' : 'web',
    consent_version: state.version,
    timestamp: state.timestamp,
  };
}

function notifyNative(state: ConsentState, persisted: boolean): void {
  if (!isBrowser()) return;
  try {
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'cookie-consent',
        persisted,
        entry: buildConsentEntry(state),
        state,
      }),
    );
  } catch {
    /* not in a WebView */
  }
}

export function syncConsentToBackend(state: ConsentState): boolean {
  if (!isBrowser()) return false;
  const token = resolveToken();
  if (!token) return false;
  try {
    fetch(`${API_BASE}user/cookie-consent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(buildConsentEntry(state)),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* ignore */
  }
  return true;
}

const CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000;

export function readConsent(): ConsentState | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (!parsed || parsed.version !== CONSENT_VERSION || !parsed.categories) {
      return null;
    }
    if (Date.now() - new Date(parsed.timestamp).getTime() > CONSENT_MAX_AGE_MS) {
      window.localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    return {
      ...parsed,
      categories: categoriesForCurrentContext(parsed.categories),
    };
  } catch {
    return null;
  }
}

export function hasValidConsent(): boolean {
  return readConsent() !== null;
}

export function writeConsent(categories: ConsentCategories): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: categoriesForCurrentContext(categories),
  };

  if (isBrowser()) {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage may be unavailable */
    }
    window.dispatchEvent(
      new CustomEvent(CONSENT_CHANGED_EVENT, { detail: state }),
    );
    const persisted = syncConsentToBackend(state);
    notifyNative(state, persisted);
  }

  return state;
}

export const acceptAll = () => writeConsent(grantedForCurrentContext());
export const rejectAll = () => writeConsent(deniedForCurrentContext());

export function openCookieSettings(): void {
  if (isBrowser()) {
    window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
  }
}
